import { useEffect, useMemo, useState, type JSX } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { Link as RouterLink } from 'react-router-dom'
import type { ReviewItem, ReviewListFilters } from '../api'
import { useApi } from '../auth'
import { MatchBasisChip } from '../components/Chips'
import { ContactKeyLink } from '../components/ContactKeyLink'
import { EmptyState, ErrorPanel, LoadingPanel } from '../components/States'
import { FilterBar } from '../components/FilterBar'
import { SavedViews } from '../components/SavedViews'
import { PageHeader } from '../components/Page'
import { EM_DASH, decidedLabel, formatNumber, humanise, verdictTone } from '../format'
import { usePageTitle } from '../usePageTitle'
import { qk, useAllReviews, useJobs } from '../queries'
import { listReady, useListView } from '../useListView'

/**
 * The review queue, across every job.
 *
 * It lived only inside a job before this — Jobs, open one, find the Review tab —
 * which is three clicks deep and invisible to anyone who had not been told where
 * to look. Four hundred contacts sat undecided for exactly that reason. Review is
 * the one part of this product that REQUIRES a person, so it earns a place in
 * the nav beside the two screens that do not.
 *
 * The gate reasons are spelled out rather than shown as codes, because each one
 * is the evidence for a decision somebody is about to make.
 */

const REASON_TEXT: Record<string, string> = {
  company_mismatch:
    'No experience on this profile matches the target company. This is the check that keeps the wrong people out of an export.',
  past_employee: 'The matched role has an end date, so they have left.',
  ends_unknown: 'The role carries no end date, so whether they still work there could not be established.',
  not_decision_maker: 'The matched title is not a decision-making role.',
  no_title: 'The matched role carries no title at all, so seniority could not be judged.',
  thin_profile: 'The provider flagged this profile as thin, so there is little to judge.',
  country_mismatch: 'The person is outside the countries this job expected.',
  null_sentinel: 'A company or title field held a null sentinel such as "None".',
}

const REASONS = Object.keys(REASON_TEXT)

/** An index signature so the shared list view can read it generically. */
interface Filters {
  [key: string]: string
  status: string
  jobId: string
  search: string
  reason: string
  aiVerdict: string
  decidedBy: string
}

const EMPTY_FILTERS: Filters = {
  status: 'open',
  jobId: '',
  search: '',
  reason: '',
  aiVerdict: '',
  decidedBy: '',
}

/*
 * The filters that NARROW the queue, which the status toggle does not.
 *
 * A queue is always showing open, accepted or rejected, so counting status
 * among them would answer an empty accepted list with "no item matches these
 * filters" when the honest answer is that nothing has been accepted yet.
 */
const NARROWING: ReadonlyArray<keyof Filters> = ['jobId', 'search', 'reason', 'aiVerdict', 'decidedBy']

/**
 * The filters as they appear in the address bar.
 *
 * One mapping used in both directions, because two would drift — and a link
 * written by one and read by the other opens the wrong queue without saying so.
 */
const URL_KEYS: Record<keyof Filters, string> = {
  status: 'status',
  jobId: 'job',
  search: 'q',
  reason: 'reason',
  aiVerdict: 'ai',
  decidedBy: 'by',
}

export function ReviewsPage(): JSX.Element {
  usePageTitle('Review')
  const api = useApi()
  const queryClient = useQueryClient()
  const jobs = useJobs()

  /*
   * Filters and place, in the URL. One mechanism, shared with every other list.
   *
   * This screen renders cards rather than a grid, so it keeps its own Back and
   * Next — but where it is and how big a page is are the same question they are
   * everywhere else, and are answered in the same place.
   */
  const [pending, setPending] = useState<{ cursor: string | null; ready: boolean }>({
    cursor: null,
    ready: false,
  })
  const view = useListView<Filters>({
    empty: EMPTY_FILTERS,
    keys: URL_KEYS,
    nextCursor: pending.cursor,
    ready: pending.ready,
  })
  const { filters, applyFilters } = view
  const pageIndex = view.page
  const pageSize = view.pageSize

  // Only what is set: an empty filter on the wire is a filter the server has to
  // read and discard.
  const query: ReviewListFilters = {
    ...(filters.status ? { status: filters.status as ReviewItem['status'] } : {}),
    ...(filters.jobId ? { jobId: filters.jobId } : {}),
    ...(filters.search ? { q: filters.search } : {}),
    ...(filters.reason ? { reason: filters.reason } : {}),
    ...(filters.aiVerdict ? { aiVerdict: filters.aiVerdict } : {}),
    ...(filters.decidedBy ? { decidedBy: filters.decidedBy as 'model' | 'human' } : {}),
  }
  const reviews = useAllReviews(query, view.cursor, pageSize)
  const items = reviews.data?.items ?? []
  const nextCursor = reviews.data?.cursor ?? null
  // Settled, and settled for THIS page — see listReady: an error counts, and the
  // previous page still on screen does not.
  const ready = listReady(reviews)
  useEffect(() => {
    setPending((prev) =>
      prev.cursor === nextCursor && prev.ready === ready ? prev : { cursor: nextCursor, ready },
    )
  }, [nextCursor, ready])

  const jobName = useMemo(() => {
    const byId = new Map((jobs.data ?? []).map((j) => [j.jobId, j.name]))
    return (id: string): string => byId.get(id) ?? id.slice(0, 8)
  }, [jobs.data])

  const decide = useMutation({
    /*
     * A rejection is also applied to this person's still-open rows for the same
     * company in other jobs, and the route counts what it closed. `ReviewItem`
     * describes a stored row rather than what one decision did, so the count is
     * read off the response here. Dropping it left the reviewers on those other
     * jobs to find items resolved by "memory:<email>" with nothing anywhere
     * saying how many or why.
     */
    mutationFn: async ({ item, decision }: { item: ReviewItem; decision: 'accept' | 'reject' }) =>
      (await api.decideReview(item.jobId, item.sortKey, decision)) as ReviewItem & {
        closedElsewhere?: number
      },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.allReviews }),
        queryClient.invalidateQueries({ queryKey: qk.jobs }),
        queryClient.invalidateQueries({ queryKey: qk.contacts }),
      ])
    },
  })

  // Auto-review runs against one job, so it is offered only when the list is
  // scoped to one. Offering it unscoped would be a button that silently picked a
  // job for you.
  const auto = useMutation({
    mutationFn: () => api.autoReview(filters.jobId, 60),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.allReviews }),
        queryClient.invalidateQueries({ queryKey: qk.jobs }),
        queryClient.invalidateQueries({ queryKey: qk.contacts }),
      ])
    },
  })

  const open = filters.status === 'open'

  // Everything Clear would put back, so the button can say what it will clear
  // and go grey when there is nothing to clear. Measured against the defaults
  // rather than against '', because `status` has a default that is not empty.
  const changedFilters = (Object.keys(EMPTY_FILTERS) as Array<keyof Filters>).filter(
    (field) => filters[field] !== EMPTY_FILTERS[field],
  ).length
  const narrowing = NARROWING.filter((field) => filters[field] !== '').length

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Review"
        description="Every contact a gate could not admit on its own, across all jobs. Accepting one puts it in the export; rejecting keeps it out of this and every future run for the same company."
      />

      {reviews.isError ? <ErrorPanel error={reviews.error} onRetry={() => void reviews.refetch()} /> : null}
      {decide.isError ? <ErrorPanel error={decide.error} /> : null}
      {auto.isError ? <ErrorPanel error={auto.error} /> : null}

      {decide.data?.closedElsewhere ? (
        <Alert severity="info">
          That rejection also closed {formatNumber(decide.data.closedElsewhere)} open{' '}
          {decide.data.closedElsewhere === 1 ? 'item' : 'items'} for the same person at the same company in
          other jobs — the same question, waiting in somebody else's queue.
        </Alert>
      ) : null}

      {auto.data ? (
        /*
         * "Reviewed 0: accepted 0, rejected 0, left 0 for you" was what this
         * said to somebody looking at a queue full of items the model had
         * already judged and deliberately handed back. Every number was correct
         * and the sentence read as broken software.
         *
         * Nothing left to do is a finished job, and it is said as one — with the
         * reason, because "already reviewed" is the fact that makes the empty
         * result make sense.
         */
        auto.data.reviewed === 0 ? (
          <Alert severity="info">
            {auto.data.alreadySeen
              ? `Nothing new to review. All ${formatNumber(auto.data.alreadySeen)} open ${
                  auto.data.alreadySeen === 1 ? 'item has' : 'items have'
                } already been through the model, which escalated ${
                  auto.data.alreadySeen === 1 ? 'it' : 'them'
                } to you on purpose — they need a person, not another opinion.`
              : 'Nothing to review in this job: its queue is empty.'}
          </Alert>
        ) : (
          <Alert severity={auto.data.escalated === auto.data.reviewed ? 'info' : 'success'}>
            Reviewed {auto.data.reviewed}: accepted {auto.data.accepted}, rejected {auto.data.rejected}, left{' '}
            {auto.data.escalated} for you.
            {auto.data.remaining ? ' More remain — run it again.' : ''}
          </Alert>
        )
      ) : null}

      <FilterBar
        activeCount={changedFilters}
        primary={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filters.status}
            onChange={(_e, value: ReviewItem['status'] | null) =>
              value && applyFilters({ ...filters, status: value })
            }
          >
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="accepted">Accepted</ToggleButton>
            <ToggleButton value="rejected">Rejected</ToggleButton>
          </ToggleButtonGroup>
        }
        actions={
          <>
            <SavedViews screen="reviews" />

            {open ? (
              <Tooltip
                title={
                  filters.jobId
                    ? 'Asks the model about open items it has NOT already seen, decides the clear ones and leaves the rest for you with its reasoning attached. Contacts found from now on are reviewed as they are found, so this is mostly for older jobs. Runs about twenty seconds.'
                    : 'Pick a job first — the reviewer runs against one job at a time.'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeOutlinedIcon />}
                    disabled={!filters.jobId || auto.isPending}
                    onClick={() => auto.mutate()}
                  >
                    {auto.isPending ? 'Reviewing…' : 'Auto-review'}
                  </Button>
                </span>
              </Tooltip>
            ) : null}

            <Button size="small" disabled={changedFilters === 0} onClick={() => applyFilters(EMPTY_FILTERS)}>
              Clear {changedFilters > 0 ? `${changedFilters} ` : ''}filter{changedFilters === 1 ? '' : 's'}
            </Button>
          </>
        }
      >
        <TextField
          size="small"
          label="Company or person"
          value={filters.search}
          onChange={(e) => applyFilters({ ...filters, search: e.target.value })}
          sx={{ minWidth: 200 }}
        />

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="job-label">Job</InputLabel>
          <Select
            labelId="job-label"
            label="Job"
            value={filters.jobId}
            onChange={(e) => applyFilters({ ...filters, jobId: e.target.value })}
          >
            <MenuItem value="">All jobs</MenuItem>
            {(jobs.data ?? []).map((job) => (
              <MenuItem key={job.jobId} value={job.jobId}>
                {job.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="reason-label">Why it is here</InputLabel>
          <Select
            labelId="reason-label"
            label="Why it is here"
            value={filters.reason}
            onChange={(e) => applyFilters({ ...filters, reason: e.target.value })}
          >
            <MenuItem value="">Any reason</MenuItem>
            {REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {humanise(r)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="ai-label">AI verdict</InputLabel>
          <Select
            labelId="ai-label"
            label="AI verdict"
            value={filters.aiVerdict}
            onChange={(e) => applyFilters({ ...filters, aiVerdict: e.target.value })}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="escalate">Escalated to you</MenuItem>
            <MenuItem value="accept">Said accept</MenuItem>
            <MenuItem value="reject">Said reject</MenuItem>
            <MenuItem value="none">Not looked at yet</MenuItem>
          </Select>
        </FormControl>

        {!open ? (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="by-label">Decided by</InputLabel>
            <Select
              labelId="by-label"
              label="Decided by"
              value={filters.decidedBy}
              onChange={(e) => applyFilters({ ...filters, decidedBy: e.target.value })}
            >
              <MenuItem value="">Anyone</MenuItem>
              <MenuItem value="human">A person</MenuItem>
              <MenuItem value="model">The reviewer</MenuItem>
            </Select>
          </FormControl>
        ) : null}
      </FilterBar>

      {reviews.isLoading ? <LoadingPanel label="Loading the review queue" /> : null}

      {/*
        Not on a failed load, and not the same words whatever the filters say.
        A queue that did not load is not a queue that is empty: the reassurance
        that every contact was admitted by the gates used to render directly
        beneath the red panel saying the list had never arrived. A filter that
        matched nothing said it too, with no error above to contradict it and no
        way back to the full queue.
      */}
      {!reviews.isError && !reviews.isLoading && items.length === 0 ? (
        <Card>
          {narrowing > 0 ? (
            <EmptyState
              title="No item matches these filters"
              description="Every item in this queue was excluded. Clear the filters to see the whole queue."
              action={
                <Button variant="outlined" onClick={() => applyFilters(EMPTY_FILTERS)}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={open ? 'Nothing waiting on you' : `No ${filters.status} items`}
              description={
                open
                  ? 'Every contact found so far was admitted by the gates outright, or has already been decided. The queue fills only when a gate cannot decide on the evidence it has.'
                  : 'Decisions made on the open queue appear here.'
              }
            />
          )}
        </Card>
      ) : null}

      {/*
        The page number moves the moment Next is clicked, and the cards below
        are the PREVIOUS page until the request lands — they are kept on screen
        deliberately, so no loading panel fires to say a page is on its way.
        This is what marks the gap the footer would otherwise mislabel.
      */}
      {reviews.isFetching && !reviews.isLoading ? <LinearProgress /> : null}

      {items.map((item) => (
        <Card key={`${item.jobId}#${item.sortKey}`} sx={{ opacity: reviews.isFetching ? 0.55 : 1 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={600}>
                  {item.fullName || EM_DASH}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.title || item.employment?.title || 'No title on the matched role'}
                </Typography>
                <MatchBasisChip basis={item.matchBasis ?? item.employment?.matchBasis ?? null} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Confidence ${item.confidence.toFixed(2)}`}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                />
                {item.aiVerdict ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<AutoAwesomeOutlinedIcon />}
                    color={verdictTone(item.aiVerdict)}
                    label={`AI: ${item.aiVerdict}`}
                  />
                ) : null}
                {!open ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={item.status === 'accepted' ? 'success' : 'default'}
                    label={decidedLabel(item.status, item.decidedBy)}
                  />
                ) : null}
              </Stack>

              {/*
                Both companies, side by side, because the decision is a
                comparison. Showing only the target named the problem without
                showing it: a reviewer could not tell "the same company under
                another legal name" from "a genuinely wrong person", which is the
                whole question being asked of them.
              */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 3 }}
                sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    You are looking for
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                    {item.accountName || 'Unknown company'}
                  </Typography>
                  {item.employment?.companyDomain ? (
                    <Typography variant="caption" color="text.secondary">
                      {item.employment.companyDomain}
                    </Typography>
                  ) : null}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Their profile says they work at
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={item.employment?.companyName ? 'text.primary' : 'text.disabled'}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {item.employment?.companyName ?? 'Nothing — the profile named no employer'}
                  </Typography>
                  {item.employment?.title ? (
                    <Typography variant="caption" color="text.secondary">
                      {item.employment.title}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ContactKeyLink contactKey={item.linkedinUrl} />
                {item.employment?.companyLinkedinUrl ? (
                  <Link
                    href={item.employment.companyLinkedinUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    variant="body2"
                    className="mono"
                  >
                    {item.employment.companyLinkedinUrl.replace(/^https:\/\/(www\.)?linkedin\.com\//, '')}
                  </Link>
                ) : null}
                {/* Which job this came from, and a way back to it. Without this a
                    queue spanning every job cannot be traced to its source. */}
                <Link
                  component={RouterLink}
                  to={`/jobs/${item.jobId}`}
                  variant="body2"
                  color="text.secondary"
                >
                  {jobName(item.jobId)}
                </Link>
              </Stack>

              <Stack spacing={0.5}>
                {(item.reasons ?? []).map((reason) => (
                  <Stack key={reason} direction="row" spacing={1} alignItems="flex-start">
                    <Chip size="small" color="warning" variant="outlined" label={humanise(reason)} />
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                      {REASON_TEXT[reason] ?? 'Flagged by an admission gate.'}
                    </Typography>
                  </Stack>
                ))}
                {(item.notes ?? []).map((note, i) => (
                  <Typography key={`${note}-${i}`} variant="caption" color="text.secondary">
                    {note}
                  </Typography>
                ))}
                {item.aiReason ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pt: 0.5 }}>
                    <AutoAwesomeOutlinedIcon fontSize="small" color="disabled" />
                    <Typography variant="caption" color="text.secondary">
                      {item.aiReason}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>

              {open ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ item, decision: 'accept' })}
                  >
                    Accept
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ item, decision: 'reject' })}
                  >
                    Reject
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {/* A page, not a total: the rest is on the server and was never counted. */}
          Page {pageIndex + 1} · {formatNumber(items.length)} shown
        </Typography>

        {/*
          How many cards a page holds, and therefore how many the server is
          asked for. This screen used to request a thousand and render every one
          of them as a card, whatever the person could actually work through.
        */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="page-size-label">Per page</InputLabel>
          <Select
            labelId="page-size-label"
            label="Per page"
            value={pageSize}
            onChange={(event) => {
              // A cursor belongs to the size that produced it, so this starts
              // again rather than resuming and skipping rows.
              view.paging.onChange({ page: 0, pageSize: Number(event.target.value) })
            }}
          >
            {[25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          disabled={pageIndex === 0 || reviews.isFetching}
          onClick={() => view.paging.onChange({ page: Math.max(0, pageIndex - 1), pageSize })}
        >
          Back
        </Button>
        <Button
          size="small"
          disabled={nextCursor === null || reviews.isFetching}
          onClick={() => view.paging.onChange({ page: pageIndex + 1, pageSize })}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  )
}
